// The editor's selection model: ONE discriminated union instead of
// seven parallel state channels. Exactly one thing is "selected" at a
// time — an overlay annotation set, a select-mode text range, a content
// image / vector fill / marquee region, or an open
// content-text edit session. `none` is the empty state.
//
// Multi-select ready: the `annots` variant carries an id ARRAY even
// though today's interactions only ever put one id in it.
import type { FillInfo, Quad, RegionItem } from "./protocol.ts";
import type { ActiveTextEdit } from "./ui-types.ts";

export type Box = [number, number, number, number];

export type Selection =
  | { kind: "none" }
  /** Overlay annotations; `editingId` is the one with an open editor. */
  | { kind: "annots"; ids: readonly string[]; editingId: string | null }
  /** Select-mode text range (Ctrl+C copies it). */
  | { kind: "text"; pageIndex: number; quads: Quad[]; text: string }
  /** A selected content image. */
  | { kind: "image"; pageIndex: number; bbox: Box }
  /** A selected vector fill block. */
  | { kind: "fill"; pageIndex: number; fill: FillInfo }
  /** A marquee multi-object region. */
  | { kind: "region"; pageIndex: number; items: RegionItem[] }
  /** An in-progress content-text edit (owns the overlay input). */
  | { kind: "textEdit"; edit: ActiveTextEdit };

export const NO_SELECTION: Selection = { kind: "none" };

/** Clear the selection only if it is of `kind` — the union equivalent
 *  of the legacy per-channel `setX(null)`. */
export function clearKind(s: Selection, kind: Selection["kind"]): Selection {
  return s.kind === kind ? NO_SELECTION : s;
}

/** The single selected annotation id (null when none / multi). */
export function primaryAnnotId(s: Selection): string | null {
  return s.kind === "annots" ? (s.ids[0] ?? null) : null;
}

/** True while document content — not the annotation overlay — is
 *  selected. Modifier+click extends these into a multi-selection. */
export function hasObjectSelection(s: Selection): boolean {
  return s.kind === "image" || s.kind === "fill" || s.kind === "region";
}

/** Toggle one object in/out of a region item list. Ids are positional
 *  within the current baseline, which is exactly what regionMove
 *  consumes, so identity is a plain id match. */
export function toggleRegionItem(
  items: readonly RegionItem[],
  item: RegionItem
): RegionItem[] {
  return items.some((i) => i.id === item.id)
    ? items.filter((i) => i.id !== item.id)
    : [...items, item];
}

/** Union of two item lists, first-seen order (marquee adds to what's
 *  already selected without duplicating). */
export function mergeRegionItems(
  items: readonly RegionItem[],
  add: readonly RegionItem[]
): RegionItem[] {
  const seen = new Set(items.map((i) => i.id));
  return [...items, ...add.filter((i) => !seen.has(i.id) && (seen.add(i.id), true))];
}

/** Bounding box of an item set (null when empty). */
export function regionBBox(items: readonly RegionItem[]): Box | null {
  if (items.length === 0) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const i of items) {
    x0 = Math.min(x0, i.bbox[0]);
    y0 = Math.min(y0, i.bbox[1]);
    x1 = Math.max(x1, i.bbox[2]);
    y1 = Math.max(y1, i.bbox[3]);
  }
  return [x0, y0, x1, y1];
}

/** Move an item's box without touching its id. */
export function shiftRegionItem(i: RegionItem, dx: number, dy: number): RegionItem {
  return {
    ...i,
    bbox: [i.bbox[0] + dx, i.bbox[1] + dy, i.bbox[2] + dx, i.bbox[3] + dy],
  };
}

/** An annotation id as a selection item id, and back. */
export const annotItemId = (id: string): string => `a:${id}`;
export const isAnnotItem = (i: RegionItem): boolean => i.kind === "annot";
export const annotIdOf = (i: RegionItem): string => i.id.slice(2);

/** Split a mixed selection into the halves that move differently: the
 *  overlay (annotations, translated client-side) and the document
 *  (content + form fields, rewritten by the worker). */
export function splitRegionItems(items: readonly RegionItem[]): {
  annots: RegionItem[];
  doc: RegionItem[];
} {
  return {
    annots: items.filter(isAnnotItem),
    doc: items.filter((i) => !isAnnotItem(i)),
  };
}

/** Human summary for the selection frame ("2 texts, 1 field"). */
export function regionLabel(items: readonly RegionItem[]): string {
  const counts = new Map<RegionItem["kind"], number>();
  for (const i of items) counts.set(i.kind, (counts.get(i.kind) ?? 0) + 1);
  const noun: Record<RegionItem["kind"], string> = {
    text: "text",
    image: "image",
    fill: "block",
    widget: "field",
    annot: "markup",
  };
  return (["text", "image", "fill", "widget", "annot"] as const)
    .filter((k) => counts.get(k))
    .map((k) => {
      const n = counts.get(k)!;
      return `${n} ${noun[k]}${n === 1 ? "" : "s"}`;
    })
    .join(", ");
}
